import { useMemo, useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import PageCommandContextMenu from '../commands/PageCommandContextMenu.jsx'
import PropTypes from 'prop-types'
import { Trash2 } from 'lucide-react'
import AssetListTable from '../assetList/AssetListTable.jsx'
import { watchlistSections } from '../../lib/watchlistSections.js'
import PrivateValue from '../PrivateValue.jsx'
import { formatOCC, isOCCSymbol, parseOCC } from '../../lib/occParser.js'
import { useOptionSessionClock } from '../../hooks/useOptionSessionClock.js'
import { optionDisplayPrice } from '../../lib/marketData/optionDisplayPrice.js'
import { isOptionListCard, optionListSession } from '../../lib/marketData/optionListSession.js'

const DEFAULT_COLUMNS = ['symbol', 'last', 'changePercent', 'extendedHours', 'quantity', 'pnl']
const COLUMN_WIDTH_STORAGE_KEY = 'erevis.positionAssetList.columnWidths.v1'

const LIFECYCLE = {
  active: { id: 'active', label: 'Active' },
  working: { id: 'working', label: 'Working' },
  idea: { id: 'ideas', label: 'Ideas' },
  ideas: { id: 'ideas', label: 'Ideas' },
  planned: { id: 'ideas', label: 'Ideas' },
  closed: { id: 'recently-closed', label: 'Recently Closed' },
  'recently-closed': { id: 'recently-closed', label: 'Recently Closed' },
}

function rowId(card, manualList = false) {
  if (manualList && card.manualMembershipId) return String(card.manualMembershipId)
  return String(card.projectIds?.[0] || card.manualMembershipId || card.rowKey || card.symbol)
}

function normalizedSession(value) {
  const session = String(value || '').trim().toLowerCase()
  if (session === 'pre') return 'pre-market'
  if (session === 'post') return 'post-market'
  if (session === 'rth' || session === 'open') return 'regular'
  return session || null
}

function price(value) {
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null
}

function signedPercent(value) {
  if (value == null || value === '') return '—'
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '—'
  return `${numeric >= 0 ? '+' : ''}${numeric.toFixed(2)}%`
}

function optionContractLabel(occ) {
  const mm = occ.expiry.slice(4, 6)
  const dd = occ.expiry.slice(6, 8)
  const yy = occ.expiry.slice(2, 4)
  return `${mm}/${dd}/${yy} ${occ.strike} ${occ.right}`
}

function changePercent(card, currentPrice, regularPrice, occ) {
  const supplied = card.dayChangePct
  if (supplied != null && supplied !== '' && Number.isFinite(Number(supplied))) return Number(supplied)

  let previousClose = regularPrice
  const quantity = Number(card.totalShares)
  const dayPnl = card.dayPnl == null || card.dayPnl === '' ? null : Number(card.dayPnl)
  if (
    previousClose == null
    && currentPrice != null
    && Number.isFinite(quantity)
    && quantity !== 0
    && dayPnl != null
    && Number.isFinite(dayPnl)
  ) {
    const multiplier = card.isOption || occ ? 100 : 1
    previousClose = currentPrice - (dayPnl / (quantity * multiplier))
  }

  if (currentPrice == null || previousClose == null || !(previousClose > 0)) return null
  return ((currentPrice - previousClose) / previousClose) * 100
}

function percentFromReference(currentPrice, referencePrice) {
  if (currentPrice == null || referencePrice == null || !(referencePrice > 0)) return null
  return ((currentPrice - referencePrice) / referencePrice) * 100
}

function money(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '—'
  return `${numeric >= 0 ? '+' : '−'}$${Math.abs(numeric).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`
}

function tone(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric === 0) return 'text-inkMuted'
  return numeric > 0 ? 'text-forest' : 'text-ember'
}

function adaptCards(cards, now, manualList) {
  const cardBySymbol = new Map(cards.map(card => [String(card.symbol || '').toUpperCase(), card]))
  return cards.map((card) => {
    const lifecycle = LIFECYCLE[String(card.lifecycleState || card.stage || '').toLowerCase()] || null
    const option = isOptionListCard(card)
    const display = option ? null : card._equityDisplay
    const session = option ? optionListSession(card, now)
      : normalizedSession(display?.session || card._quoteSession || card.session)
    const hasOptionQuote = option && Object.hasOwn(card, '_last')
    const currentPrice = hasOptionQuote ? optionDisplayPrice({ last: card._last, sessionClose: card._sessionClose, lastRth: card._lastRth, close: card.prevClose }, session) : price(card.currentPrice)
    const regularPrice = price(card._sessionClose) || price(card._lastRth)
    const previousClose = price(card.prevClose)
    const officialClose = regularPrice || previousClose
    const extended = ['pre-market', 'post-market', 'overnight'].includes(session)
    const occ = isOCCSymbol(card.symbol) ? parseOCC(card.symbol) : null
    const providerMark = price(card._mark)
    const extendedPrice = extended
      && !option
      && currentPrice != null
      && officialClose != null
      && Math.abs(currentPrice - officialClose) < 0.000001
      && providerMark != null
      ? providerMark
      : currentPrice
    const completedRthChange = extended && !option
      ? percentFromReference(regularPrice, previousClose)
      : null
    const parentCard = occ ? cardBySymbol.get(String(occ.underlying || '').toUpperCase()) : null
    const id = rowId(card, manualList)
    const parentId = card.parentInstrumentId || (parentCard ? rowId(parentCard, manualList) : null)
    return {
      id,
      parentId,
      symbol: occ ? (parentId ? optionContractLabel(occ) : formatOCC(card.symbol)) : card.symbol,
      displayName: card.displayName || null,
      assetType: option ? 'option' : card.assetType,
      lifecycle: lifecycle ? [lifecycle.label] : [],
      groupId: lifecycle?.id || 'manual',
      quote: {
        price: display ? display.headlinePrice : currentPrice,
        available: display ? display.headlinePrice != null : currentPrice != null,
        session,
        freshnessReason: display ? display.freshnessReason : card.freshnessReason,
        freshness: display?.freshness || card.freshness || (currentPrice == null ? 'unavailable' : 'live'),
      },
      last: display ? display.last : (extended ? officialClose : currentPrice),
      changePercent: hasOptionQuote ? percentFromReference(currentPrice, previousClose) : display ? display.changePercent : extended && !option
        ? completedRthChange
        : changePercent(card, currentPrice, previousClose, occ),
      // The equity close comparison is not an option extended-session quote.
      extendedHours: option ? null : display ? display.extendedHours : (extended ? percentFromReference(extendedPrice, officialClose) : null),
      quantity: card.totalShares,
      pnl: card.unrealizedPnl,
      originalCard: card,
    }
  })
}

function privateNumber(value, formatter = valueToFormat => valueToFormat) {
  if (value == null || value === '' || !Number.isFinite(Number(value))) return '—'
  return <PrivateValue>{formatter(Number(value))}</PrivateValue>
}

const COLUMN_DEFINITIONS = {
  symbol: {
    id: 'asset',
    header: 'Symbol',
    accessor: row => row.symbol,
    sortable: true,
    width: 180,
    minWidth: 72,
  },
  last: {
    id: 'last',
    header: 'Last',
    accessor: row => row.last,
    sortValue: row => row.last,
    render: row => row.last == null ? '—' : Number(row.last).toFixed(2),
    align: 'right',
    sortable: true,
    width: 68,
    minWidth: 58,
    className: 'whitespace-nowrap',
  },
  changePercent: {
    id: 'changePercent',
    header: 'Chng%',
    accessor: row => row.changePercent,
    sortValue: row => row.changePercent,
    render: row => <span className={tone(row.changePercent)}>{signedPercent(row.changePercent)}</span>,
    align: 'right',
    sortable: true,
    width: 76,
    minWidth: 64,
    className: 'whitespace-nowrap',
  },
  extendedHours: {
    id: 'extendedHours',
    header: 'Ext',
    accessor: row => row.extendedHours,
    sortValue: row => row.extendedHours,
    render: row => <span className={tone(row.extendedHours)}>{signedPercent(row.extendedHours)}</span>,
    align: 'right',
    sortable: true,
    width: 54,
    minWidth: 48,
    className: 'whitespace-nowrap',
  },
  quantity: {
    id: 'quantity',
    header: 'Qty',
    accessor: row => row.quantity,
    sortValue: row => row.quantity,
    render: row => privateNumber(row.quantity, value => value.toLocaleString('en-US', { maximumFractionDigits: 4 })),
    align: 'right',
    sortable: true,
    width: 54,
    minWidth: 48,
    className: 'whitespace-nowrap',
  },
  pnl: {
    id: 'pnl',
    header: 'PnL',
    accessor: row => row.pnl,
    sortValue: row => row.pnl,
    render: row => <span className="text-ink">{privateNumber(row.pnl, money)}</span>,
    align: 'right',
    sortable: true,
    width: 73,
    minWidth: 62,
    className: 'whitespace-nowrap',
  },
}

function tableSort(sort) {
  if (!sort || sort.field === 'manual') return { mode: 'manual' }
  return {
    mode: 'column',
    columnId: sort.field === 'symbol' ? 'asset' : sort.field,
    direction: sort.direction === 'desc' ? 'desc' : 'asc',
  }
}

export default function PositionAssetList({
  cards,
  view,
  currentSymbol = null,
  configuration = null,
  classifications = null,
  onOpenCard = null,
  onVisibleCardsChange = null,
  onRemoveManualAsset = null,
  onDeleteIdea = null,
  onRemoveIdea = null,
  ideaSelectMode = false,
  selectedIdeaKeys = null,
  onToggleIdea = null,
  onSortChange = null,
  onRowReorder = null,
  manualRowOrder = null,
  reorderScope = '',
  onGroupToggle = null,
  status = 'ready',
  error = null,
}) {
  const [ideaMenu, setIdeaMenu] = useState(null)
  const optionSessionClock = useOptionSessionClock(cards.some(isOptionListCard))
  const closeIdeaMenu = useCallback(() => setIdeaMenu(null), [])
  useEffect(() => { closeIdeaMenu() }, [view.id, view.kind, closeIdeaMenu])
  const handleRowContextMenu = (row, event) => {
    if (event.shiftKey || !onRemoveIdea || !['idea', 'planned'].includes(String(row.originalCard.stage || '').toLowerCase())) return
    event.preventDefault()
    event.stopPropagation()
    setIdeaMenu({ x: event.clientX, y: event.clientY, assetSymbol: row.originalCard.symbol, card: row.originalCard })
  }
  const adaptedRows = useMemo(() => {
    const adapted = adaptCards(cards, optionSessionClock, view.kind === 'manual-list')
    if (!Array.isArray(manualRowOrder)) return adapted
    const rank = new Map(manualRowOrder.map((id, index) => [id, index]))
    return adapted.sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity))
  }, [cards, manualRowOrder, optionSessionClock, view.kind])
  const grouped = classifications?.enabled
    ? watchlistSections(adaptedRows, classifications.profiles, classifications.pending) : null
  const rows = grouped?.rows || adaptedRows
  const columnKeys = configuration?.columns?.length ? configuration.columns : DEFAULT_COLUMNS
  const columns = useMemo(
    () => columnKeys.map(key => COLUMN_DEFINITIONS[key]).filter(Boolean),
    [columnKeys],
  )
  const lifecycleSections = configuration?.lifecycleSections || {}
  const groups = grouped?.groups || (view.kind === 'all-assets'
    ? [
        { id: 'active', label: 'Active', collapsed: lifecycleSections.active === false },
        { id: 'working', label: 'Working', collapsed: lifecycleSections.working === false },
        { id: 'ideas', label: 'Ideas', collapsed: lifecycleSections.ideas === false },
        { id: 'recently-closed', label: 'Recently Closed', collapsed: lifecycleSections.recentlyClosed === false },
      ]
    : [])
  const selected = rows.find(row => row.originalCard.symbol === currentSymbol)?.id || null
  const rowActions = []
  if (ideaSelectMode && onToggleIdea) {
    rowActions.push({
      id: 'select-idea',
      label: 'Select idea',
      ariaLabel: row => `Select ${row.originalCard.symbol} idea`,
      visible: row => ['idea', 'planned'].includes(String(row.originalCard.stage || '').toLowerCase()),
      icon: row => selectedIdeaKeys?.has(rowId(row.originalCard)) ? '☑' : '☐',
      onSelect: row => onToggleIdea(row.originalCard),
    })
  }
  if (view.kind === 'manual-list' && onRemoveManualAsset) {
    rowActions.push({
        id: 'remove',
        label: `Remove from ${view.label}`,
        ariaLabel: row => view.id === 'legacy-watchlist'
          ? `Remove ${row.originalCard.symbol} from watchlist`
          : `Remove from ${view.label} for ${row.originalCard.symbol}`,
        visible: row => Boolean(row.originalCard.manualMembershipId || row.originalCard.stage === 'watchlist'),
        icon: <Trash2 size={12} aria-hidden="true" />,
        onSelect: row => onRemoveManualAsset(row.originalCard),
      })
  }
  if (onDeleteIdea) {
    rowActions.push({
      id: 'delete-idea',
      label: 'Delete idea',
      icon: <Trash2 size={12} aria-hidden="true" />,
      ariaLabel: row => `Delete ${row.originalCard.symbol} idea`,
      visible: row => ['idea', 'planned'].includes(String(row.originalCard.stage || '').toLowerCase()),
      onSelect: row => onDeleteIdea(row.originalCard),
    })
  }

  return (
    <>
      <AssetListTable
        containerClassName="max-h-full"
        ariaLabel={`${view.label || 'Position Workspace'} assets`}
        view={view}
        rows={rows}
        columns={columns}
        groups={groups}
        sort={manualRowOrder ? { mode: 'manual' } : tableSort(configuration?.sort)}
        onRowReorder={onRowReorder}
        reorderScope={reorderScope}
        onSortChange={onSortChange ? (next) => onSortChange({
          field: next.mode === 'manual' ? 'manual' : next.columnId === 'asset' ? 'symbol' : next.columnId,
          direction: next.direction || 'asc',
        }) : null}
        density={configuration?.density || 'compact'}
        selectedRowId={selected}
        onRowSelect={row => onOpenCard?.(row.originalCard)}
        onRowNavigate={row => onOpenCard?.(row.originalCard)}
        onVisibleRowsChange={onVisibleCardsChange
          ? rows => onVisibleCardsChange(rows.map(row => row.originalCard))
          : null}
        onRowContextMenu={handleRowContextMenu}
        rowActions={rowActions}
        onGroupToggle={!grouped && onGroupToggle ? (groupId, nextCollapsed) => onGroupToggle(groupId, !nextCollapsed) : null}
        status={status}
        error={error}
        emptyMessage={`No assets in ${view.label || 'this view'}`}
        columnWidthStorageKey={COLUMN_WIDTH_STORAGE_KEY}
      />
      {ideaMenu && createPortal(
        <PageCommandContextMenu
          menu={ideaMenu}
          onClose={closeIdeaMenu}
          onRemoveFromIdeas={() => onRemoveIdea(ideaMenu.card)}
        />,
        document.body,
      )}
    </>
  )
}

PositionAssetList.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  view: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    label: PropTypes.string,
    listId: PropTypes.string,
  }).isRequired,
  currentSymbol: PropTypes.string,
  classifications: PropTypes.object,
  configuration: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.string),
    density: PropTypes.oneOf(['compact', 'comfortable']),
    sort: PropTypes.shape({ field: PropTypes.string, direction: PropTypes.string }),
    lifecycleSections: PropTypes.object,
  }),
  onOpenCard: PropTypes.func,
  onVisibleCardsChange: PropTypes.func,
  onRemoveManualAsset: PropTypes.func,
  onDeleteIdea: PropTypes.func,
  onRemoveIdea: PropTypes.func,
  ideaSelectMode: PropTypes.bool,
  selectedIdeaKeys: PropTypes.instanceOf(Set),
  onToggleIdea: PropTypes.func,
  onSortChange: PropTypes.func,
  onRowReorder: PropTypes.func,
  manualRowOrder: PropTypes.arrayOf(PropTypes.string),
  reorderScope: PropTypes.string,
  onGroupToggle: PropTypes.func,
  status: PropTypes.oneOf(['ready', 'loading', 'error']),
  error: PropTypes.instanceOf(Error),
}
